/**
 * WEB-2586: «этот ответ сервера — про ресурс, который открыт в редакторе?».
 *
 * Ответ записи (сохранение, публикация, смена языка) может прийти уже после
 * того, как редактор сброшен и открыт другой квиз (сохранение при уходе,
 * WEB-2551). Принять его значит подменить метку времени черновика открытого
 * ресурса чужой — и следующее сохранение получит ложный 409 «страница
 * устарела», а признаки «идёт сохранение» и «есть несохранённые правки»
 * погаснут у не того. Обработчики успеха в `editorSlice.ts` принимают ответ
 * только через эту проверку; хранилище проверки (`review/reviewSlice.ts`)
 * делает то же своей `sameResource` — второй конец правила, его держит сторож
 * `openResource.ends.web2586.test.ts`.
 */
import type { EditorState } from './state';

export function isOpenResource(
  state: Pick<EditorState, 'editorType' | 'resourceId'>,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
): boolean {
  return state.editorType === kind && typeof id === 'string' && state.resourceId === id;
}

/**
 * Начало и отказ записи — тем же правилом, что и успех: состояние меняется,
 * только если запись про открытый ресурс (`next` — готовое новое состояние).
 * Без этого сохранение при уходе (WEB-2551), которое страница диспатчит уже
 * ПОСЛЕ сброса редактора, ставило «идёт сохранение» пустому состоянию, а
 * чужой успех его уже не снимал — у следующего квиза автосохранение
 * выключалось навсегда; чужой отказ публикации писал смертельную `error` в
 * открытый редактор (экран ошибки по чужой вине).
 */
export function whenOpen(
  state: EditorState,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
  next: () => EditorState,
  /** WEB-2561: meta санка сохранения — ответ только своего сеанса. */
  meta?: unknown,
): EditorState {
  return isOpenResource(state, kind, id) && isSameWriteSession(state, meta) ? next() : state;
}

/**
 * Успех сохранения или публикации: только открытого ресурса. Двигает снимок
 * `serverUpdatedAt` на свежий `updatedAt` ответа (WEB-1333 п.5 — следующая
 * сверка версий идёт от него; публикация пишет запись повторно со своим
 * штампом, без этого следующее автосохранение даёт ложный 409). Сохранение
 * черновика (`clearDirty`) ещё и гасит «не сохранено». Чужой ответ — состояние
 * без изменений.
 */
export function acceptWriteResponse(
  state: EditorState,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
  payload: unknown,
  clearDirty: boolean,
  /** WEB-2561: meta санка сохранения — ответ только своего сеанса. */
  meta?: unknown,
): EditorState {
  if (!isOpenResource(state, kind, id) || !isSameWriteSession(state, meta)) return state;
  const stamp = (payload as { updatedAt?: string } | undefined)?.updatedAt;
  return {
    ...state,
    isSaving: false,
    ...(clearDirty && { isDirty: false }),
    serverUpdatedAt: stamp ?? state.serverUpdatedAt ?? null,
  };
}

/**
 * WEB-2230: успех публикации — как `acceptWriteResponse` без гашения «не
 * сохранено», и открытый ресурс больше не «ни разу не публиковался»: перевод
 * теперь пойдёт с опубликованного, предупреждение окна перевода про черновик
 * стало бы неправдой. Ответ чужого ресурса признак не трогает (WEB-2586).
 */
export function acceptPublishResponse(
  state: EditorState,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
  payload: unknown,
): EditorState {
  const next = acceptWriteResponse(state, kind, id, payload, false);
  return next === state ? state : { ...next, neverPublished: false };
}

/**
 * Ревизия ранней части пачки 49 (з6): «этот ответ ОТКРЫТИЯ — от последнего
 * запроса открытия?». Номер запроса кладёт `pending` (`loadRequestId`) и он
 * остаётся до следующего открытия или сброса редактора; успех и отказ
 * принимаются только с тем же номером: медленный ответ квиза А, пришедший
 * после открытия квиза Б (до или после ответа Б), иначе подменял редактор Б
 * целиком, а его отказ сворачивал Б в экран ошибки. Номера нет (редактор
 * сброшен и ничего не открывает; прямой диспатч ответа в тестах) — ответ
 * принимается, как прежде: подменять нечего.
 * Второй конец правила — `openStarted` хранилища проверки (reviewSlice).
 */
export function isCurrentLoad(
  state: Pick<EditorState, 'loadRequestId'>,
  requestId: string | undefined,
): boolean {
  // Действие, собранное руками по публичному типу без `meta` (так открывают
  // ресурс тесты, например surface-layers-seam.web942), — без номера
  // запроса: судить не по чему, принимается, как до правила.
  if (requestId === undefined) return true;
  return state.loadRequestId == null || state.loadRequestId === requestId;
}

/**
 * WEB-2561: сеанс записи. Сверка ресурса выше («тот же вид и тот же id») не
 * отличает ПРОШЛЫЙ сеанс редактора того же квиза от нынешнего: человек ушёл
 * из квиза A и вернулся в него, пока сохранение при уходе (WEB-2551) или
 * обычное сохранение ещё в пути. Ответ той записи сдвигал отметку версии
 * открытого редактора на свой штамп, хотя содержимое в окне могло быть
 * прочитано ДО неё, — следующее сохранение молча ложилось поверх ушедших
 * правок вместо честного 409; её отказ снимал «идёт сохранение» у записи
 * нового сеанса.
 *
 * Санк сохранения снимает при старте, держит ли редактор этот ресурс и в
 * каком сеансе открытия (`openSession` — номер запроса, начавшего открытие;
 * перечитывание того же ресурса сеанс не меняет), и несёт снимок в meta
 * успеха и отказа. Обработчики (`whenOpen` / `acceptWriteResponse` с meta)
 * принимают ответ, только если сеанс тот же. Запись, ушедшая из сброшенного
 * редактора (сохранение при уходе), не принимается никаким.
 */
export interface WriteSession {
  /** На старте санка редактор держал этот ресурс. */
  held: boolean;
  /** Сеанс открытия на старте санка (у тест-фикстур без поля — undefined). */
  session: string | undefined;
}

export interface WriteSessionMeta {
  writeSession?: WriteSession;
}

/** Снимок для meta санка записи — на его старте. */
export function writeSessionOf(
  editor: Pick<EditorState, 'editorType' | 'resourceId' | 'openSession'>,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
): WriteSession {
  const held = isOpenResource(editor, kind, id);
  return { held, session: held ? (editor.openSession ?? undefined) : undefined };
}

/**
 * Ответ из того же сеанса, что открыт? Без снимка в meta (действие собрано
 * руками по публичному типу, санки публикации и смены языка) — судить не по
 * чему, принимается, как до правила.
 */
export function isSameWriteSession(
  state: Pick<EditorState, 'openSession'>,
  meta: unknown,
): boolean {
  const ws = (meta as WriteSessionMeta | undefined)?.writeSession;
  if (ws === undefined) return true;
  return ws.held && ws.session === (state.openSession ?? undefined);
}

/**
 * Сеанс после начала открытия: тот же ресурс уже открыт (перечитывание —
 * useDraftReload, откат версии, отмена хода) — сеанс прежний, иначе (первое
 * открытие, открытие после сброса, другой ресурс) — новый, номер запроса.
 */
export function nextOpenSession(
  state: Pick<EditorState, 'editorType' | 'resourceId' | 'openSession'>,
  kind: 'quiz' | 'paywall',
  id: string | undefined,
  requestId: string | undefined,
): string | undefined {
  return isOpenResource(state, kind, id) ? (state.openSession ?? undefined) : requestId;
}
